import type { SoftwareOffer } from '@/lib/seo/jsonld'

/**
 * The ONE source of truth for every public price string — mirrored into the
 * `/pricing` page, the `/` pricing teaser, the `/for-clubs` deep-dive, and the
 * JSON-LD `offers`, so copy and structured data can never drift.
 *
 * Numbers follow the owner-approved unified pricing model (2026-08-02) as
 * amended by **D3.6 (2026-08-03/04)**: the permanent Free plan is REMOVED and
 * replaced by a one-month Trial counted per PERSON, after which the account
 * freezes (reads stay, creates pause). Backend tier keys:
 * trial / organizer / coach / t2(Club) / t3(National Center) / federation.
 */
export type PricingTierName =
  | 'Trial'
  | 'Organizer'
  | 'Coach'
  | 'Club'
  | 'National Center'
  | 'Federation Center'

export interface PricingTier {
  name: PricingTierName
  /** Display price, e.g. 'Free', '$8.88', '$68'. */
  price: string
  /** Cadence caption shown after the price ('/mo', 'for 1 month'). */
  cadence?: string
  /** Court allowance line, e.g. 'Up to 5 courts'. */
  courts: string
  /** Events & programs allowance, e.g. 'Unlimited events'. */
  events: string
  /**
   * The plan buys COMPETITIONS and nothing else. Mirrors the backend tier flag
   * `PLAN_TIERS.<tier>.competitionsOnly` (backend/src/modules/credits/plans.mjs),
   * enforced by ownerAccount/competitionsOnly.mjs.
   *
   * Copy derives WHICH tiers are excepted from this flag (see
   * `COMPETITIONS_ONLY_TIERS`) so a second narrow tier cannot leave a sentence
   * naming only the first. WHAT such a tier is refused — court time, program
   * seats, memberships — is deliberately NOT data: it lives in a comment on the
   * backend gate, so a human writes that half of the sentence.
   */
  competitionsOnly?: boolean
  /** AI credits line, e.g. '150 credits / month'. */
  credits: string
  whoFor: string
  /**
   * A dimmer line under `whoFor`. Trial only: it is freeze-disclosure #1 of 3 and
   * it sits on the card the visitor is about to click (UX brief 2026-08-04 §2.2).
   */
  footnote?: string
  /** Numeric price (USD) for JSON-LD. Every tier now carries a real number. */
  offerPrice: string
  /** JSON-LD `Offer.description`. */
  offerDescription: string
}

export const PRICING_TIERS: PricingTier[] = [
  {
    name: 'Trial',
    price: 'Free',
    cadence: 'for 1 month',
    courts: 'Up to 5 courts',
    events: 'Unlimited events',
    credits: '20 credits, one time',
    whoFor: "The Club plan's features, for one month. One trial per person — no card required.",
    footnote:
      'After the month: everything you built stays visible. Creating new bookings and events pauses until you pick a plan.',
    offerPrice: '0',
    offerDescription:
      "One month of the Club plan's features — up to 5 courts, unlimited events, and 20 one-time AI credits.",
  },
  {
    name: 'Organizer',
    price: '$8.88',
    cadence: '/mo',
    courts: 'Unlimited courts',
    // COMPETITIONS, not "events". The backend flag was renamed `eventsOnly` →
    // `competitionsOnly` [2026-08-05 — owner] precisely because a clinic reads as
    // an event: a program (clinic / master class / camp) is COACHING and starts at
    // Coach. This copy said "and clinics" on both the card and the JSON-LD offer,
    // selling the one thing the gate refuses. See
    // backend/src/modules/ownerAccount/competitionsOnly.mjs.
    events: 'Unlimited competitions',
    competitionsOnly: true,
    credits: 'No monthly credits',
    whoFor:
      'For running tournaments, leagues and ladders at any venue. Competitions only — no court bookings, clinics or membership plans.',
    offerPrice: '8.88',
    offerDescription:
      'Unlimited tournaments, leagues and ladders at any venue. No court bookings, clinics or membership plans. Billed monthly.',
  },
  {
    name: 'Coach',
    price: '$16.88',
    cadence: '/mo',
    courts: '1 court',
    events: 'Unlimited events',
    credits: '20 credits / month',
    whoFor: 'A personal booking calendar your students book directly, plus unlimited events.',
    offerPrice: '16.88',
    offerDescription:
      'Unlimited events plus a personal lesson-booking calendar students book directly. Billed monthly.',
  },
  {
    name: 'Club',
    price: '$68',
    cadence: '/mo',
    courts: '2–7 courts',
    events: 'Unlimited events',
    credits: '150 credits / month',
    whoFor: 'For a growing club with a full schedule.',
    offerPrice: '68',
    offerDescription: '2–7 courts, unlimited events, and 150 AI credits per month. Billed monthly.',
  },
  {
    name: 'National Center',
    price: '$98',
    cadence: '/mo',
    courts: '8–14 courts',
    events: 'Unlimited events',
    credits: '300 credits / month',
    whoFor: 'For a busy multi-court club.',
    offerPrice: '98',
    offerDescription: '8–14 courts, unlimited events, and 300 AI credits per month. Billed monthly.',
  },
  {
    name: 'Federation Center',
    price: '$199',
    cadence: '/mo',
    courts: '15+ courts',
    events: 'Unlimited events',
    credits: '450 credits / month',
    whoFor: 'For the largest clubs and federations.',
    offerPrice: '199',
    offerDescription: '15+ courts, unlimited events, and 450 AI credits per month. Billed monthly.',
  },
]

/**
 * A tier's display price ('$8.88'), for the PROSE that would otherwise retype it.
 *
 * Every sentence quoting a price reads it from here. A hand-typed number a few
 * lines from its own source is how the `/pricing` meta description advertised
 * Coach at $8.88 when it is $16.88 — and a meta description is read in Google's
 * result, before the page can correct anyone. The tier cards never needed this
 * (they render `tier.price` directly); prose does.
 */
export function priceOf(name: PricingTierName): string {
  const tier = PRICING_TIERS.find((entry) => entry.name === name)
  if (!tier) throw new Error(`priceOf: unknown pricing tier "${name}"`)
  return tier.price
}

/**
 * The bare allowance out of a `credits` line — '150 credits / month' → '150'.
 * `null` for a tier that carries none ('No monthly credits'), so prose can never
 * silently print a 0 for Organizer.
 */
function creditFigure(line: string): string | null {
  return /^(\d[\d,]*)\b/.exec(line)?.[1] ?? null
}

/**
 * A tier's AI-credit allowance as a bare number ('150'), for the PROSE that would
 * otherwise retype it — "Coach adds 20 a month, Club 150, National Center 300".
 *
 * The credit half of `priceOf`, and it exists for the same reason: four copy bugs
 * in two days were all a hand-written sentence restating a number that already sat
 * a few lines away as data. Read out of the tier's own `credits` line rather than
 * stored beside it — a second field is a second thing to forget.
 */
export function creditsOf(name: PricingTierName): string {
  const tier = PRICING_TIERS.find((entry) => entry.name === name)
  if (!tier) throw new Error(`creditsOf: unknown pricing tier "${name}"`)
  const figure = creditFigure(tier.credits)
  if (!figure) throw new Error(`creditsOf: tier "${name}" has no allowance ("${tier.credits}")`)
  return figure
}

/**
 * HOW THE SIX ARE ARRANGED — data, because two surfaces arrange them and both
 * used to do it by hand. The home teaser took `PRICING_TIERS.slice(0, 3)` of the
 * club plans and showed "Trial · Club · National Center": a ladder whose third
 * rung is eight courts, offered to a visitor who does not own one yet. What a
 * newcomer needs is the next step, not the ceiling.
 *
 * The COURT ladder is every tier priced by courts — i.e. everything the
 * competitions-only plans are not. Its first three rungs (free month → one court
 * → a small club) are the steps someone can actually start on; the rest is where
 * a club goes once it outgrows them. Slice, never a typed list of names, so a new
 * rung lands in the right group on the day it is added.
 *
 * `*_PLANS` are whole tiers (what a card renders); the `*_TIERS` lists below are
 * names (what prose interpolates).
 */
const COURT_LADDER = PRICING_TIERS.filter((tier) => !tier.competitionsOnly)

/** Trial → Coach → Club: the three steps the site leads with. */
export const ENTRY_PLANS: readonly PricingTier[] = COURT_LADDER.slice(0, 3)

/** The rungs above the entry steps — shown in full, carried quieter. */
export const GROWTH_PLANS: readonly PricingTier[] = COURT_LADDER.slice(3)

/** Off the court axis entirely — shown to one side of the ladder. */
export const COMPETITIONS_ONLY_PLANS: readonly PricingTier[] = PRICING_TIERS.filter(
  (tier) => tier.competitionsOnly,
)

/**
 * The tiers whose plan buys competitions and nothing else — the exception the
 * `/pricing` copy names. Derived, never typed: `PricingPlans` used to write
 * "except Organizer" in prose, which a second narrow tier would have left stale.
 */
export const COMPETITIONS_ONLY_TIERS: readonly PricingTierName[] = COMPETITIONS_ONLY_PLANS.map(
  (tier) => tier.name,
)

/**
 * 🚨 D3.6 HARD CONSTRAINT [2026-08-04 — owner] — Organizer must NOT be sellable
 * until the "events only" restriction is ENFORCED in backend code. Today
 * `organizer` carries `courtCap: null`, so buying it at $8.88 grants UNLIMITED
 * courts with nothing withheld — strictly worse than the cap it replaced. The
 * tier is still DISPLAYED (a visitor has to be able to price themselves), but no
 * CTA may start a checkout for it, and it is excluded from the in-app plan
 * ladder (`CHECKOUT_TIER_ORDER`) and refused defensively in `startCheckout`.
 *
 * Delete the entry the day the events-only gate ships — nothing else changes.
 */
export const NON_SELLABLE_TIERS: readonly PricingTierName[] = ['Organizer']

export function isTierSellable(name: PricingTierName): boolean {
  return !NON_SELLABLE_TIERS.includes(name)
}

/**
 * JSON-LD `offers` — every tier now carries a published number, so all six emit.
 * Passed to `buildSoftwareApplicationJsonLd` on `/`, `/pricing`, and `/for-clubs`.
 */
export const PRICING_OFFERS: SoftwareOffer[] = PRICING_TIERS.map((tier) => ({
  name: tier.name,
  price: tier.offerPrice,
  priceCurrency: 'USD',
  description: tier.offerDescription,
}))

/** Top-up packs — available on any paid plan (display-only). */
export const PRICING_TOPUPS = [
  { price: '$30', credits: '150 credits' },
  { price: '$58', credits: '300 credits' },
] as const

/**
 * Every credit figure that currently exists — the tier allowances plus the top-up
 * pack sizes. The set `scripts/pricing-copy-check.mts` validates copy against, the
 * way it already validates price literals: a number of credits quoted anywhere in
 * the copy has to be a number of credits somebody can actually get.
 */
export const CREDIT_FIGURES: readonly string[] = [
  ...PRICING_TIERS.map((tier) => creditFigure(tier.credits)),
  ...PRICING_TOPUPS.map((pack) => creditFigure(pack.credits)),
].filter((figure): figure is string => figure !== null)

/**
 * The tiers that carry an allowance at all — the roll-call the credit copy on
 * `/pricing` recites ("Coach adds 20 a month, Club 150, …"). Derived so the check
 * can hold both surfaces to the CURRENT list: a new tier with credits must appear
 * in that sentence, and Organizer, which has none, must not.
 */
export const CREDIT_BEARING_TIERS: readonly PricingTierName[] = PRICING_TIERS.filter(
  (tier) => creditFigure(tier.credits) !== null,
).map((tier) => tier.name)

/**
 * Platform billing is display-only until a processor is live. `false` keeps the
 * paid tiers in the "online plans opening shortly" State A (CTA → start the free
 * month); State B is switchable via this one flag — the pricing card reads it and
 * no other FE change is needed.
 *
 * NOTE: this is a MARKETING-page compile-time constant only. In-app surfaces must
 * drive their copy off the live `billingConfigured` field from `GET /billing`, so
 * the day a processor goes live the app flips with no deploy (UX brief §7.3).
 */
export const BILLING_CONFIGURED = false

/** Interim destination for sales/help CTAs (Flag F1 — owner to confirm a real
 *  support address before public launch; mirrors the footer). */
export const SALES_CONTACT_MAILTO = 'mailto:rutennis.com@gmail.com'
